/**
 * The administration's words for the domain's codes.
 *
 * The API sends `PREMIUM_PLUS`, `CONSUMED`, `SUBSCRIPTION_LIMIT`; an
 * administrator reads "Premium Plus", "Used", "Set aside by a plan limit".
 * Every screen that shows one of these asks here, so a code has one name in
 * the whole application instead of one per screen — and a screen that forgot
 * to map one shows a raw enum nobody chose to show.
 *
 * Presentation only. Nothing here changes a value the API sends or receives;
 * the raw codes stay available in each screen's technical details.
 */
import {
  formatDate as formatDateIn,
  formatDateRange as formatDateRangeIn,
  formatDateTime as formatDateTimeIn,
  formatDay as formatDayIn,
  type Language,
  type Translate,
} from '@athena/i18n'

/** A plan's name. The same dictionary entries the tutor's plan screen reads. */
export function planName(t: Translate, code: string): string {
  switch (code) {
    case 'FREE':
      return t('subscription.planName.FREE')
    case 'PREMIUM':
      return t('subscription.planName.PREMIUM')
    case 'PREMIUM_PLUS':
      return t('subscription.planName.PREMIUM_PLUS')
    default:
      // A plan this build has no name for: a newer API against an older
      // bundle. The code is a poor label and a better one than blank.
      return code
  }
}

export type Role = 'TUTOR' | 'STUDENT' | 'ADMIN'

export function roleName(t: Translate, role: Role): string {
  switch (role) {
    case 'TUTOR':
      return t('admin.roleTutor')
    case 'STUDENT':
      return t('admin.roleStudent')
    case 'ADMIN':
      return t('admin.roleAdmin')
  }
}

/** Profiles in one fixed order, whatever order the API listed them in. */
export const ROLE_ORDER: readonly Role[] = ['TUTOR', 'STUDENT', 'ADMIN']

export function sortRoles(roles: readonly Role[]): Role[] {
  return ROLE_ORDER.filter((role) => roles.includes(role))
}

/** One entry of the AI ledger, as the event it records. */
export function usageKindName(t: Translate, kind: string): string {
  switch (kind) {
    case 'GENERATION':
      return t('admin.usageKindGeneration')
    case 'ADJUSTMENT':
      return t('admin.usageKindAdjustment')
    case 'EXTRACTION':
      return t('admin.usageKindExtraction')
    default:
      return kind
  }
}

/**
 * Where an entry got to. A failed call is told apart from a delivered one:
 * released means the window's free failures covered it, consumed means they
 * had run out and it was charged.
 */
export function usageStatusName(t: Translate, status: string, failed = false): string {
  if (failed && status === 'RELEASED') return t('admin.usageStatusFailedFree')
  if (failed && status === 'CONSUMED') return t('admin.usageStatusFailedCharged')
  switch (status) {
    case 'CONSUMED':
      return t('admin.usageStatusConsumed')
    case 'RESERVED':
      return t('admin.usageStatusReserved')
    case 'RELEASED':
      return t('admin.usageStatusReleased')
    default:
      return status
  }
}

/**
 * How many credits an entry moved, in words.
 *
 * An adjustment's sign is the ledger's: negative returns credits to the tutor
 * and positive takes them back, which is the rule the adjustment form states.
 * So "−5" on an adjustment is "5 credits returned", and anything positive is
 * a count of credits, whatever the entry's status says happened to them.
 */
export function creditsText(t: Translate, kind: string, amount: number): string {
  if (kind === 'ADJUSTMENT' && amount < 0) {
    const returned = Math.abs(amount)
    return returned === 1
      ? t('admin.creditsReturnedOne')
      : t('admin.creditsReturnedOther', [String(returned)])
  }
  return countOf(t, Math.abs(amount), 'admin.creditsOne', 'admin.creditsOther')
}

export function suspensionReasonName(t: Translate, reason: string | null): string {
  switch (reason) {
    case 'SUBSCRIPTION_LIMIT':
      return t('admin.suspendedByPlan')
    case 'TUTOR_REQUEST':
      return t('admin.suspendedByTutor')
    default:
      return t('admin.suspended')
  }
}

export function languageName(t: Translate, code: string): string {
  if (code === 'ES') return t('language.es')
  if (code === 'EN') return t('language.en')
  return code
}

/**
 * A count, choosing between a `…One` and a `…Other` key — the product's
 * whole pluralization model, for the two languages that share that split.
 */
export function countOf(
  t: Translate,
  count: number,
  one: Parameters<Translate>[0],
  other: Parameters<Translate>[0],
): string {
  return count === 1 ? t(one, [String(count)]) : t(other, [String(count)])
}

/**
 * A date in the page's language, or a dash for none.
 *
 * The conventions — "29 sept 2026", "Sep 29, 2026", which locale each language
 * formats with — are `@athena/i18n`'s, shared with the tutor and student app,
 * so an administrator and the tutor they are helping read one date the same
 * way.
 */
export function formatDate(value: string | null | undefined, language: Language): string {
  if (!value) return '—'
  return formatDateIn(value, language)
}

export function formatDateTime(value: string | null | undefined, language: Language): string {
  if (!value) return '—'
  return formatDateTimeIn(value, language)
}

/** Two dates as one range, without repeating the month or year they share. */
export function formatDateRange(start: string, end: string, language: Language): string {
  return formatDateRangeIn(start, end, language)
}

/** A `yyyy-mm-dd` field's own day, as it reads in the page's language — not shifted by a time zone. */
export function formatDay(value: string, language: Language): string {
  if (!value) return '—'
  return formatDayIn(`${value}T00:00:00Z`, language, { utc: true })
}
